import {
  type ComponentProps,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';

const SCROLL_THRESHOLD = 4;

const FADE_EDGE_CLASS =
  'pointer-events-none absolute inset-x-0 z-10 h-16 transition-opacity duration-200';

const TOP_FADE_CLASS = cn(
  FADE_EDGE_CLASS,
  'top-0 bg-gradient-to-b from-background via-background/90 to-transparent',
);

const BOTTOM_FADE_CLASS = cn(
  FADE_EDGE_CLASS,
  'bottom-0 bg-gradient-to-t from-background via-background/90 to-transparent',
);

interface ScrollFadeProps extends Omit<ComponentProps<'div'>, 'children'> {
  children: ReactNode;
  fadeClassName?: string;
}

export function ScrollFade({
  children,
  className,
  fadeClassName,
  onScroll,
  onScrollCapture,
  ...props
}: ScrollFadeProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    setShowTopFade(scrollTop > SCROLL_THRESHOLD);
    setShowBottomFade(scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD);
  }, []);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      updateFades();
      onScroll?.(event);
    },
    [onScroll, updateFades],
  );

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const contentEl = contentRef.current;
    if (!scrollEl) return;

    updateFades();

    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(scrollEl);
    if (contentEl) {
      resizeObserver.observe(contentEl);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateFades]);

  return (
    <div className={cn('relative h-full min-h-0 overflow-hidden', className)} {...props}>
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-y-auto"
        onScroll={handleScroll}
        onScrollCapture={onScrollCapture}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      <div
        aria-hidden
        className={cn(TOP_FADE_CLASS, showTopFade ? 'opacity-100' : 'opacity-0', fadeClassName)}
      />
      <div
        aria-hidden
        className={cn(
          BOTTOM_FADE_CLASS,
          showBottomFade ? 'opacity-100' : 'opacity-0',
          fadeClassName,
        )}
      />
    </div>
  );
}
